import type { ChinaMapRegionId } from "@-/dev.improve.haniverse.data.china-map";
import { mapDownloadSchema, type MapDownload } from "@-/dev.improve.haniverse.data.map-downloads";
import { anySignal } from "@-/com.npmjs.any-signal";
import { fetchWithDeadline, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import type { MapDownloadStore } from "#map-download-storage-types";

const TRAILING_SLASHES_RE = /\/+$/;
export const mapDownloadQueryKey = (workerUrl: string, regionId: ChinaMapRegionId) =>
  ["map-download", workerUrl.replace(TRAILING_SLASHES_RE, ""), regionId] as const;

/** No import of a generated map is reachable from this app-facing module. */
export function createMapDownloadService({
  fetchPack = (url, signal) =>
    fetchWithDeadline(url, {
      cache: "no-store",
      deadline: 25_000,
      operation: "Download map",
      signal,
    }),
  store,
  workerUrl,
}: {
  fetchPack?: (url: string, signal?: AbortSignal) => Promise<Response>;
  store: MapDownloadStore;
  workerUrl: string;
}) {
  const baseUrl = workerUrl.replace(TRAILING_SLASHES_RE, "");
  const keyOf = (regionId: ChinaMapRegionId) => `${baseUrl}/maps/v1/${regionId}.json`;
  const storageWait = <T>(operation: string, run: (signal: AbortSignal) => Promise<T>) =>
    withDeadline({ deadline: 10_000, operation }, run);

  function parse(json: string, regionId: ChinaMapRegionId): MapDownload {
    let value: unknown;
    try {
      value = JSON.parse(json);
    } catch (error) {
      throw new Error("Map download was incomplete. Please retry.", { cause: error });
    }
    const result = mapDownloadSchema.safeParse(value);
    if (!result.success) {
      throw new Error("Map download could not be read. Please retry or update the app.", {
        cause: result.error,
      });
    }
    const pack = result.data;
    if (pack.regionId !== regionId) {
      throw new Error("The downloaded map belongs to another region. Please retry.");
    }
    return pack;
  }

  async function fetchRegion(
    regionId: ChinaMapRegionId,
    signal?: AbortSignal,
  ): Promise<MapDownload> {
    const response = await withDeadline(
      { deadline: 25_000, operation: "Load online map" },
      (deadlineSignal) => fetchPack(keyOf(regionId), anySignal([signal, deadlineSignal])),
    );
    if (!response.ok) {
      throw new Error(
        `Map download failed (${response.status}). Connect to the internet and retry.`,
      );
    }
    const json = await withDeadline(
      { deadline: 10_000, operation: "Read map download" },
      async () => response.text(),
    );
    signal?.throwIfAborted();
    return parse(json, regionId);
  }

  return {
    async download(regionId: ChinaMapRegionId, signal?: AbortSignal): Promise<MapDownload> {
      // Always fetch afresh, including when replacing an older download. Validate the whole
      // response before writing; failures leave the previous valid download intact.
      const pack = await fetchRegion(regionId, signal);
      const json = JSON.stringify(pack);
      const info = { bytes: new TextEncoder().encode(json).byteLength, downloadedAt: Date.now() };
      await storageWait("Save offline map", (deadlineSignal) =>
        store.write(keyOf(regionId), json, info, anySignal([signal, deadlineSignal])),
      );
      return pack;
    },
    info(regionId: ChinaMapRegionId) {
      return storageWait("Read offline map status", () => store.info(keyOf(regionId)));
    },
    async load(regionId: ChinaMapRegionId, signal?: AbortSignal): Promise<MapDownload> {
      let json: string | null;
      try {
        json = await storageWait("Read offline map", () => store.read(keyOf(regionId)));
      } catch {
        // A browser that disallows persistent storage can still use maps online.
        json = null;
      }
      signal?.throwIfAborted();
      if (json !== null) {
        try {
          return parse(json, regionId);
        } catch {
          // An interrupted/old-format disk entry must not wedge the map page. Re-fetch it;
          // this never converts an online visit into an implicit persistent download.
          await storageWait("Remove unreadable offline map", () =>
            store.remove(keyOf(regionId)),
          ).catch(() => {});
        }
      }
      return fetchRegion(regionId, signal);
    },
    remove(regionId: ChinaMapRegionId) {
      return storageWait("Remove offline map", () => store.remove(keyOf(regionId)));
    },
  };
}
